<template>
  <v-card class="linha-detalhe-panel d-flex flex-column" outlined>
    <template v-if="hasEntityPreview">
      <v-toolbar dense flat color="primary" dark class="flex-grow-0">
        <v-toolbar-title class="subtitle-1 text-truncate">
          {{ previewTitle }}
        </v-toolbar-title>
      </v-toolbar>
      <div class="linha-detalhe-panel__tabs-bar d-flex align-center flex-grow-0">
        <!-- A ordem destas abas deve espelhar o computed tabKeys. -->
        <v-tabs v-model="tab" background-color="grey lighten-4" class="flex-grow-1">
          <v-tab>{{ $t('crudLinhas.detail.tabs.map') }}</v-tab>
          <v-tab v-if="showUnidadeComparacaoTab">{{ $t('crudLinhas.detail.tabs.comparison') }}</v-tab>
          <v-tab v-if="showParametrosTab">{{ $t('crudLinhas.detail.tabs.parameters') }}</v-tab>
        </v-tabs>
      </div>
      <v-tabs-items v-model="tab" class="linha-detalhe-panel__tabs flex-grow-1">
        <v-tab-item class="linha-detalhe-panel__tab-item linha-detalhe-panel__tab-item--map fill-height">
          <v-card-text class="linha-detalhe-panel__preview d-flex flex-column flex-grow-1 pa-0">
            <v-progress-linear
              v-if="previewType === 'unit' && unitPreviewLoading"
              indeterminate
              color="primary"
              class="mb-3 flex-grow-0"
            />
            <div class="linha-detalhe-panel__preview-map flex-grow-1">
              <area-map-editor
                v-if="previewType === 'area'"
                ref="entityPreviewMap"
                :areas="previewItemsResolved"
                :area="areaMapData && areaMapData.area"
                :unidades="(areaMapData && areaMapData.unidades) || []"
                :sugestoes="(areaMapData && areaMapData.sugestoes) || []"
                :can-edit="canEdit"
                :can-insert="canInsert"
                class="linha-detalhe-panel__map-editor"
                @map-action="$emit('map-action', $event)"
              >
                <template #create-entity><slot name="create-entity" /></template>
              </area-map-editor>
              <recursos-operacao-map-editor
                v-else
                ref="entityPreviewMap"
                :entity-preview-type="previewType"
                :entity-preview-item="previewItem"
                :entity-preview-items="previewItemsResolved"
                :entity-preview-rotas="resolvedEntityPreviewRotas"
                :entity-preview-cerca="resolvedEntityPreviewCerca"
                :entity-preview-cercas-linha="resolvedEntityPreviewCercasLinha"
                :entity-preview-fit-prefer-cerca="resolvedEntityPreviewFitPreferCerca"
                :entity-preview-unit-groups="unitPreviewGroups"
                :additional-linhas="additionalLinhas"
                :map-context="activeModuleValue"
                :token="token"
                :can-edit="canEdit"
                :can-insert="canInsert"
                class="linha-detalhe-panel__map-editor"
                @map-action="$emit('map-action', $event)"
                @preview-changed="$emit('preview-changed', $event)"
                @unidade-linhas-changed="onUnidadeLinhasChanged"
              >
                <template #create-entity><slot name="create-entity" /></template>
              </recursos-operacao-map-editor>
            </div>
          </v-card-text>
        </v-tab-item>
        <v-tab-item
          v-if="showUnidadeComparacaoTab"
          class="linha-detalhe-panel__tab-item linha-detalhe-panel__tab-item--scroll"
        >
          <unidade-comparacao-rotas
            :unidade="previewItem"
            :sentidos="sentidos"
            :can-edit="canEdit"
            :active="tabKeys[tab] === 'comparison'"
          />
        </v-tab-item>
        <v-tab-item
          v-if="showParametrosTab"
          class="linha-detalhe-panel__tab-item linha-detalhe-panel__tab-item--scroll"
        >
          <recursos-operacionais-parametros-pane
            :can-edit="canEdit"
            :has-content="hasParametrosSlot"
            :total="parametrosTotal"
            :page="parametrosPage"
            :label="parametrosLabel"
            @save="$emit('parametros-save')"
            @update:page="$emit('update:parametrosPage', $event)"
          >
            <slot name="parametros" />
          </recursos-operacionais-parametros-pane>
        </v-tab-item>
      </v-tabs-items>
    </template>
    <template v-else-if="!linha || !linha.id_linha">
      <v-card-text class="d-flex align-center justify-center flex-grow-1">
        <div class="text-center grey--text">
          <v-icon large color="grey lighten-1">{{ emptyStateIcon }}</v-icon>
          <div class="mt-2">{{ $t('crudLinhas.detail.selectResource') }}</div>
        </div>
      </v-card-text>
    </template>
    <template v-else>
      <v-toolbar dense flat color="primary" dark class="flex-grow-0">
        <v-toolbar-title class="subtitle-1 text-truncate">
          {{ linha.cod_linha }} — {{ linha.nome_linha }}
        </v-toolbar-title>
      </v-toolbar>
      <div class="linha-detalhe-panel__tabs-bar d-flex align-center flex-grow-0">
        <v-tabs v-model="tab" background-color="grey lighten-4" class="flex-grow-1">
          <v-tab>{{ $t('crudLinhas.detail.tabs.map') }}</v-tab>
          <v-tab>{{ $t('crudLinhas.detail.tabs.routes') }}</v-tab>
          <v-tab>{{ $t('crudLinhas.detail.tabs.fences') }}</v-tab>
          <v-tab>{{ $t('crudLinhas.detail.tabs.schedule') }}</v-tab>
          <v-tab>{{ $t('crudLinhas.tabs.shifts') }}</v-tab>
          <v-tab>{{ $t('crudLinhas.detail.tabs.passengers') }}</v-tab>
          <v-tab>{{ $t('crudLinhas.detail.tabs.comparison') }}</v-tab>
          <v-tab v-if="showParametrosTab">{{ $t('crudLinhas.detail.tabs.parameters') }}</v-tab>
        </v-tabs>
        <div class="linha-detalhe-panel__sentido flex-grow-0 flex-shrink-0 px-2">
          <v-select
            v-model="sentidoSelecionado"
            :items="sentidos"
            item-text="descricao_sentido"
            item-value="id_sentido"
            :label="$t('crudLinhas.map.direction')"
            dense
            outlined
            hide-details
            :disabled="!sentidos.length"
          />
        </div>
      </div>
      <v-tabs-items v-model="tab" class="linha-detalhe-panel__tabs flex-grow-1">
        <!-- Mapa: preenche o painel; wheel rola a página (sticky até o fim da tabela) -->
        <v-tab-item class="linha-detalhe-panel__tab-item linha-detalhe-panel__tab-item--map fill-height">
          <div class="linha-detalhe-panel__map-wrap d-flex flex-column fill-height">
            <recursos-operacao-map-editor
              ref="mapEditor"
              :linha="linha"
              :additional-linhas="additionalLinhas"
              :sentidos="sentidos"
              :sentido-selecionado="sentidoSelecionado"
              :map-context="activeModuleValue"
              :token="token"
              :can-edit="canEdit"
              :can-insert="canInsert"
              :passageiros="marcadoresPassageiros"
              :tipos-cerca="tiposCerca"
              :tipos-rota="tiposRota"
              :historico-sessao-id="historicoSessaoId"
              :historico-sessao-origem-id="historicoSessaoOrigemId"
              :selecao-total="parametrosTotal"
              :selecao-page="parametrosPage"
              :selecao-label="parametrosLabel"
              :cerca-create-select-route="cercaCreateSelectRoute"
              :cerca-create-route-id.sync="cercaCreateRouteIdInternal"
              class="linha-detalhe-panel__map-editor"
              @update:sentidoSelecionado="onSentidoFromMap"
              @passenger-click="onPassengerClick"
              @cercas-changed="onCercasChanged"
              @rotas-changed="onRotasChanged"
              @map-action="$emit('map-action', $event)"
              @update:selecaoPage="$emit('update:parametrosPage', $event)"
            >
              <template #create-entity><slot name="create-entity" /></template>
            </recursos-operacao-map-editor>
          </div>
        </v-tab-item>
        <v-tab-item class="linha-detalhe-panel__tab-item linha-detalhe-panel__tab-item--scroll">
          <div class="pa-2">
            <!-- Vinculo de rota ja cadastrada: mesmo formato do cadastro de turnos. -->
            <v-form v-if="canEdit" ref="formAddRota" class="mb-3">
              <v-row dense align="center">
                <v-col cols="12" sm="4">
                  <LazySelect
                    ref="rotaAssocSelect"
                    get-url="getRotaslazy"
                    :text="rotaAssocSelectText"
                    value="id_rota"
                    :label="$t('crudLinhas.form.routes.route')"
                    @select="onRotaAssocSelect"
                  />
                </v-col>
                <v-col cols="12" sm="2">
                  <v-select
                    v-model="itemRotaAssoc.id_tipo_rota"
                    :items="tiposRota"
                    item-text="descricao_tipo_rota"
                    item-value="id_tipo_rota"
                    :label="$t('crudLinhas.form.routes.routeType')"
                    dense
                    outlined
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="12" sm="2">
                  <v-select
                    v-model="itemRotaAssoc.id_sentido"
                    :items="sentidos"
                    item-text="descricao_sentido"
                    item-value="id_sentido"
                    :label="$t('crudLinhas.form.routes.direction')"
                    dense
                    outlined
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model="itemRotaAssoc.cod_integracao"
                    :label="$t('crudLinhas.headers.routes.integrationCode')"
                    maxlength="50"
                    clearable
                    dense
                    outlined
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="12" sm="2" class="text-sm-right">
                  <v-btn
                    v-if="editingRotaAssoc"
                    icon
                    small
                    class="mr-1"
                    :disabled="savingRotaAssoc"
                    :title="$t('button.cancel')"
                    @click="cancelEditRotaAssoc"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                  <v-btn
                    color="primary"
                    dark
                    small
                    fab
                    :loading="savingRotaAssoc"
                    :disabled="savingRotaAssoc"
                    @click="saveRotaAssoc"
                  >
                    <v-icon v-if="!editingRotaAssoc">mdi-plus</v-icon>
                    <v-icon v-else>mdi-pencil</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
            <merus-data-table
              :headers="headersRotas"
              :items="rotasVisiveis"
              :loading="loadingRotas"
              :loading-text="$t('crudLinhas.loading')"
              hide-columns-button
              hide-configure-columns-button
              cod-tabela="LINHAS__ROTAS"
              @update:headers="headersRotas = $event"
            >
              <template v-slot:toolbar>
                <cadastro-toolbar
                  :title="$t('crudLinhas.form.routes.title')"
                  :token="token"
                  :items="rotasLinha"
                  :csv-title="$t('crudLinhas.form.routes.title')"
                  :pdf-title="$t('crudLinhas.form.routes.title')"
                  :pdf-headers="headersRotas"
                  :pdf-filename="$t('crudLinhas.form.routes.title')"
                  :csv-headers="headersRotasCSV"
                  :help-dialog-csv-fields="helpCSVRotas"
                  :help-dialog-csv-model="$t('crudLinhas.form.routes.csvModel')"
                  :endpoint-upload-csv="canEdit ? '/api/v1.0/processacsvrotaslinha/' : null"
                  hide-search
                  @saveBulk="saveBulkRotas"
                />
              </template>
              <template v-slot:item.actions="{ item }">
                <v-icon
                  v-if="canEdit"
                  small
                  class="mr-2"
                  color="green"
                  @click="editRotaAssoc(item)"
                >mdi-pencil</v-icon>
                <v-icon
                  v-if="canEdit"
                  small
                  color="red"
                  @click="deleteRotaFromTable(item)"
                >mdi-delete</v-icon>
              </template>
            </merus-data-table>
          </div>
        </v-tab-item>
        <v-tab-item class="linha-detalhe-panel__tab-item linha-detalhe-panel__tab-item--scroll">
          <div class="pa-2">
            <!-- Vinculo de cerca ja cadastrada: mesmo formato do cadastro de turnos. -->
            <v-form v-if="canEdit" ref="formAddCerca" class="mb-3">
              <v-row dense align="center">
                <v-col cols="12" sm="4">
                  <LazySelect
                    ref="cercaAssocSelect"
                    get-url="getCercaslazy"
                    :text="cercaAssocSelectText"
                    value="id_cerca"
                    :label="$t('crudLinhas.form.fences.fence')"
                    @select="onCercaAssocSelect"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="itemCercaAssoc.id_tipo_cerca"
                    :items="tiposCerca"
                    item-text="descricao_tipo_cerca"
                    item-value="id_tipo_cerca"
                    :label="$t('crudLinhas.form.fences.fenceType')"
                    dense
                    outlined
                    hide-details="auto"
                    @change="onTipoCercaAssocChange"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="itemCercaAssoc.id_sentido"
                    :items="sentidos"
                    item-text="descricao_sentido"
                    item-value="id_sentido"
                    :label="$t('crudLinhas.form.fences.direction')"
                    dense
                    outlined
                    hide-details="auto"
                  />
                </v-col>
              </v-row>
              <v-row dense align="center" class="mt-1">
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="itemCercaAssoc.cod_integracao"
                    :label="$t('crudLinhas.headers.fences.integrationCode')"
                    maxlength="50"
                    clearable
                    dense
                    outlined
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="6" sm="2">
                  <v-text-field
                    v-model.number="itemCercaAssoc.ordem_parada"
                    :label="$t('crudLinhas.form.fences.order')"
                    type="number"
                    min="1"
                    dense
                    outlined
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="6" sm="2">
                  <v-text-field
                    v-model.number="itemCercaAssoc.tempo_chegada_minutos"
                    :label="$t('crudLinhas.form.fences.arrival')"
                    type="number"
                    min="0"
                    dense
                    outlined
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="8" sm="3">
                  <v-switch
                    v-model="cercaAssocShowSinotico"
                    :label="$t('crudLinhas.form.fences.synoptic')"
                    dense
                    hide-details
                    class="mt-0"
                  />
                </v-col>
                <v-col cols="4" sm="2" class="text-sm-right">
                  <v-btn
                    v-if="editingCercaAssoc"
                    icon
                    small
                    class="mr-1"
                    :disabled="savingCercaAssoc"
                    :title="$t('button.cancel')"
                    @click="cancelEditCercaAssoc"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                  <v-btn
                    color="primary"
                    dark
                    small
                    fab
                    :loading="savingCercaAssoc"
                    :disabled="savingCercaAssoc"
                    @click="saveCercaAssoc"
                  >
                    <v-icon v-if="!editingCercaAssoc">mdi-plus</v-icon>
                    <v-icon v-else>mdi-pencil</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
            <merus-data-table
              :headers="headersCercas"
              :items="cercasVisiveis"
              :loading="loadingCercas"
              :loading-text="$t('crudLinhas.loading')"
              :default-multi-sort="defaultMultiSortCercas"
              hide-columns-button
              hide-configure-columns-button
              cod-tabela="LINHAS__CERCAS"
              @update:headers="headersCercas = $event"
            >
              <template v-slot:toolbar>
                <cadastro-toolbar
                  :title="$t('crudLinhas.form.fences.title')"
                  :token="token"
                  :items="cercasLinha"
                  :csv-title="$t('crudLinhas.form.fences.csvTitle')"
                  :pdf-title="$t('crudLinhas.form.fences.pdfTitle')"
                  :pdf-headers="headersCercas"
                  :pdf-filename="$t('crudLinhas.form.fences.pdfFilename')"
                  :csv-headers="headersCercasCSV"
                  :help-dialog-csv-fields="helpCSVCercas"
                  :help-dialog-csv-model="$t('crudLinhas.form.fences.csvModel')"
                  :endpoint-upload-csv="canEdit ? '/api/v1.0/processacsvcercaslinha/' : null"
                  hide-search
                  @saveBulk="saveBulkCercas"
                />
              </template>
              <template v-slot:item.actions="{ item }">
                <v-icon
                  v-if="canEdit"
                  small
                  class="mr-2"
                  color="green"
                  @click="editCercaAssoc(item)"
                >mdi-pencil</v-icon>
                <v-icon
                  v-if="canEdit"
                  small
                  color="red"
                  @click="deleteCercaFromTable(item)"
                >mdi-delete</v-icon>
              </template>
            </merus-data-table>
          </div>
        </v-tab-item>
        <!-- Grade/Passageiros: scroll interno até o fim da tabela -->
        <v-tab-item class="linha-detalhe-panel__tab-item linha-detalhe-panel__tab-item--scroll">
          <linha-grade-horaria
            :linha="linha"
            :token="token"
            :sentido-selecionado="sentidoSelecionado"
          />
        </v-tab-item>
        <v-tab-item class="linha-detalhe-panel__tab-item linha-detalhe-panel__tab-item--scroll">
          <linha-turnos-table
            :linha="linha"
            :token="token"
            :can-edit="canEdit"
            @changed="$emit('turnos-changed')"
          />
        </v-tab-item>
        <v-tab-item class="linha-detalhe-panel__tab-item linha-detalhe-panel__tab-item--scroll">
          <linha-passageiros-table
            :linha="linha"
            :passageiros="passageirosVisiveis"
            :loading="loadingPassageiros"
            :can-edit="canEdit"
            @refresh="loadPassageiros"
            @edit-passenger="openEditAllocation"
            @deallocate="requestDeallocate"
            @allocate="openBulkAllocation"
            @allocate-group="openGroupAllocation"
          />
        </v-tab-item>
        <v-tab-item class="linha-detalhe-panel__tab-item linha-detalhe-panel__tab-item--scroll">
          <linha-comparacao-rotas
            :linha="linha"
            :sentidos="sentidos"
            :sentido-selecionado.sync="sentidoSelecionado"
            :can-edit="canEdit"
            :active="tabKeys[tab] === 'comparison'"
          />
        </v-tab-item>
        <v-tab-item
          v-if="showParametrosTab"
          class="linha-detalhe-panel__tab-item linha-detalhe-panel__tab-item--scroll"
        >
          <recursos-operacionais-parametros-pane
            :can-edit="canEdit"
            :has-content="hasParametrosSlot"
            :total="parametrosTotal"
            :page="parametrosPage"
            :label="parametrosLabel"
            @save="$emit('parametros-save')"
            @update:page="$emit('update:parametrosPage', $event)"
          >
            <slot name="parametros" />
          </recursos-operacionais-parametros-pane>
        </v-tab-item>
      </v-tabs-items>

      <aloca-passageiro-linha-dialog
        v-model="allocationDialog"
        :passageiro="passageiroSelecionado"
        :linha="linha"
        :historico-sessao-id="historicoSessaoId"
        :historico-sessao-origem-id="historicoSessaoOrigemId"
        :cercas-linha="cercasLinha"
        :grupos="grupos"
        :candidatos="candidatosAlocacao"
        :loading-candidatos="loadingCandidatos"
        @saved="onAllocated"
        @proceed-allocation="onProceedAllocation"
        @cercas-changed="onCercasChanged"
        @rotas-changed="onRotasChanged"
      />
      <RoteirizacaoAlocacaoDialog
        ref="roteirizacaoAllocationDialog"
        :token="token"
        :validated-addresses="allocationValidatedAddresses"
        :passengers="candidatosAlocacao"
        :groups="gruposParaAlocacao"
        :poll-process="pollSchedulerForAllocation"
        :line-capacity-fallback="lineCapacityFallback"
        :historico-sessao-id="historicoSessaoId"
        :historico-sessao-origem-id="historicoSessaoOrigemId"
        historico-origem="CRUD_LINHAS"
        @saved="onRoteirizacaoAllocationSaved"
        @cercas-changed="onCercasChanged"
        @rotas-changed="onRotasChanged"
      />
      <aloca-grupo-linha-dialog
        v-model="groupAllocationDialog"
        :linha="linha"
        :historico-sessao-id="historicoSessaoId"
        :historico-sessao-origem-id="historicoSessaoOrigemId"
        @saved="onAllocated"
      />
      <v-dialog v-model="confirmDeleteCercaDialog" persistent max-width="500">
        <v-card>
          <v-card-title class="headline red--text text--accent-4">
            {{ $t('crudLinhas.messages.systemMessage') }}
          </v-card-title>
          <v-card-text class="red--text text--accent-4">
            {{ confirmDeleteCercaMessage }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="red accent-4" class="white--text" @click="cancelDeleteCerca">
              {{ $t('crudLinhas.messages.no') }}
            </v-btn>
            <v-btn color="red accent-4" class="white--text" @click="confirmDeleteCerca">
              {{ $t('crudLinhas.messages.yes') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="dialogTerminusOrder" persistent max-width="500">
        <v-card>
          <v-card-title class="headline">
            <span style="color: #d60000">{{ $t('crudLinhas.messages.systemMessage') }}</span>
          </v-card-title>
          <v-card-text>
            <span style="color: #d60000">{{ $t('crudLinhas.map.dialogTerminusOrderBody') }}</span>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="red accent-4" class="white--text" @click="dialogTerminusOrder = false">
              {{ $t('button.confirmOk') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </v-card>
</template>

<script>
import apiController from "@/utils/apiController";
import EventBus from "@/utils/eventBus";
import AreaMapEditor from "./AreaMapEditor.vue";
import RecursosOperacaoMapEditor from "./RecursosOperacaoMapEditor.vue";
import LinhaGradeHoraria from "./LinhaGradeHoraria.vue";
import LinhaTurnosTable from "./LinhaTurnosTable.vue";
import LinhaPassageirosTable from "./LinhaPassageirosTable.vue";
import LinhaComparacaoRotas from "./LinhaComparacaoRotas.vue";
import UnidadeComparacaoRotas from "../Unidades/UnidadeComparacaoRotas.vue";
import AlocaPassageiroLinhaDialog from "./AlocaPassageiroLinhaDialog.vue";
import AlocaGrupoLinhaDialog from "./AlocaGrupoLinhaDialog.vue";
import RoteirizacaoAlocacaoDialog from "@/components/Roteirizacao/RoteirizacaoAlocacaoDialog.vue";
import LazySelect from "@/components/Selects/LazySelect.vue";
import MerusDataTable from "@/components/Tables/MerusDataTable.vue";
import CadastroToolbar from "@/components/Toolbars/CadastroToolbar.vue";
import RecursosOperacionaisParametrosPane from "./RecursosOperacionaisParametrosPane.vue";
import { persistHistoricoRoteirizacaoCrudEvent } from "@/utils/historicoRoteirizacaoSnapshot";
import {
  hasTerminusOrderViolation,
  wouldBreakTerminusOrder,
} from "@/utils/allocationFenceCreation";

export default {
  name: "RecursosOperacionaisDetalhePanel",
  inject: {
    recursosOperacaoModuleDefs: { default: null },
  },
  components: {
    LazySelect,
    RecursosOperacaoMapEditor,
    AreaMapEditor,
    LinhaGradeHoraria,
    LinhaTurnosTable,
    LinhaPassageirosTable,
    LinhaComparacaoRotas,
    UnidadeComparacaoRotas,
    AlocaPassageiroLinhaDialog,
    AlocaGrupoLinhaDialog,
    RoteirizacaoAlocacaoDialog,
    MerusDataTable,
    CadastroToolbar,
    RecursosOperacionaisParametrosPane,
  },
  props: {
    linha: { type: Object, default: null },
    sentidos: { type: Array, default: () => [] },
    token: { type: String, default: null },
    canEdit: { type: Boolean, default: false },
    canInsert: { type: Boolean, default: false },
    tiposCerca: { type: Array, default: () => [] },
    tiposRota: { type: Array, default: () => [] },
    previewType: { type: String, default: null },
    previewItem: { type: Object, default: null },
    /** Todos os itens marcados na tabela (inclui o previewItem); habilita a exibição simultânea no mapa. */
    previewItems: { type: Array, default: () => [] },
    /** Outras linhas marcadas na tabela (contexto "linhas"), exibidas em sobreposição no mapa. */
    additionalLinhas: { type: Array, default: () => [] },
    initialTab: { type: Number, default: 0 },
    /** Aba inicial por chave (ver tabKeys); tem precedência sobre initialTab. */
    initialTabKey: { type: String, default: null },
    /** Quantidade de itens selecionados na tabela (paginador de parâmetros). */
    parametrosTotal: { type: Number, default: 0 },
    /** Página atual do paginador de parâmetros, base 0. */
    parametrosPage: { type: Number, default: 0 },
    parametrosLabel: { type: String, default: null },
    preferredRouteIds: { type: Array, default: () => [] },
    historicoSessaoId: { type: String, default: null },
    historicoSessaoOrigemId: { type: String, default: null },
    linhaCreatePreviewRotas: { type: Array, default: () => [] },
    linhaCreatePreviewCerca: { type: Object, default: null },
    linhaCreatePreviewCercasLinha: { type: Array, default: () => [] },
    linhaCreatePreviewPreferUnidade: { type: Boolean, default: false },
    cercaCreateSelectRoute: { type: Boolean, default: false },
    cercaCreateRouteId: { type: [Number, String], default: null },
    /** Módulo de áreas: { area, unidades, sugestoes } para o AreaMapEditor. */
    areaMapData: { type: Object, default: null },
  },
  data() {
    return {
      loadedLinhaId: null,
      tab: 0,
      /** One-shot: impede o reset de aba na próxima troca de linha (paginação). */
      preservarAbaProximaLinha: false,
      sentidoSelecionado: null,
      /** Sentido escolhido automaticamente na troca de linha; deixa de valer se o usuário trocar. */
      sentidoAutomatico: undefined,
      passageirosLinha: [],
      cercasLinha: [],
      headersCercas: [],
      rotasLinha: [],
      headersRotas: [],
      grupos: [],
      gruposCarregados: false,
      loadingPassageiros: false,
      loadingCandidatos: false,
      loadingCercas: false,
      loadingRotas: false,
      /** Formulario de vinculo de rota ja cadastrada (aba Rotas). */
      itemRotaAssoc: { id_rota: null, id_tipo_rota: null, id_sentido: null, cod_integracao: null },
      rotaAssocDetail: null,
      savingRotaAssoc: false,
      editingRotaAssoc: false,
      /** Chave (id_rota + id_sentido) da rota em edicao, que sera substituida. */
      rotaAssocOriginal: null,
      /** Formulario de vinculo de cerca ja cadastrada (aba Cercas). */
      itemCercaAssoc: {
        id_cerca: null,
        id_tipo_cerca: null,
        id_sentido: null,
        ordem_parada: 1,
        tempo_chegada_minutos: 0,
        cod_integracao: null,
      },
      cercaAssocDetail: null,
      cercaAssocShowSinotico: true,
      savingCercaAssoc: false,
      editingCercaAssoc: false,
      /** Chave (id_cerca + id_sentido + id_tipo_cerca) da cerca em edicao. */
      cercaAssocOriginal: null,
      allocationDialog: false,
      groupAllocationDialog: false,
      passageiroSelecionado: null,
      candidatosAlocacao: [],
      allocationValidatedAddresses: [],
      allocationPollProcess: null,
      schedulerPoll: { timer: null, inFlight: false },
      confirmDeleteCercaDialog: false,
      dialogTerminusOrder: false,
      cercaPendenteExclusao: null,
      unitPreviewLoading: false,
      unitPreviewFence: null,
      unitPreviewRoutes: [],
      /** Um grupo {unit, cerca, rotas} por unidade marcada, quando há seleção múltipla. */
      unitPreviewGroups: [],
      unitPreviewRequestSeq: 0,
    };
  },
  computed: {
    /** Itens a exibir no mapa: a seleção múltipla da tabela (se houver) ou só o previewItem. */
    previewItemsResolved() {
      if (Array.isArray(this.previewItems) && this.previewItems.length) return this.previewItems;
      return this.previewItem ? [this.previewItem] : [];
    },
    hasEntityPreview() {
      return ["route", "fence", "unit", "linha", "area"].includes(this.previewType);
    },
    hasParametrosSlot() {
      return !!(this.$scopedSlots.parametros || this.$slots.parametros);
    },
    /** Aba Parâmetros só existe com pelo menos um item marcado na tabela. */
    showParametrosTab() {
      return Number(this.parametrosTotal) > 0;
    },
    /** Consolidação das linhas da unidade: uma unidade em foco (a do previewItem). */
    showUnidadeComparacaoTab() {
      return this.previewType === "unit" && !!this.previewItem && this.previewItem.id_unidade != null;
    },
    cercaCreateRouteIdInternal: {
      get() {
        return this.cercaCreateRouteId;
      },
      set(val) {
        this.$emit("update:cercaCreateRouteId", val);
      },
    },
    /**
     * A ORDEM DESTA LISTA DEVE ESPELHAR A ORDEM DOS <v-tab>/<v-tab-item> NO TEMPLATE.
     * Alterou o template? Altere aqui também.
     */
    tabKeys() {
      if (this.hasEntityPreview) {
        const keys = ["map"];
        if (this.showUnidadeComparacaoTab) keys.push("comparison");
        if (this.showParametrosTab) keys.push("parameters");
        return keys;
      }
      const keys = ["map", "routes", "fences", "schedule", "shifts", "passengers", "comparison"];
      if (this.showParametrosTab) keys.push("parameters");
      return keys;
    },
    parametrosTabIndex() {
      return this.tabKeys.indexOf("parameters");
    },
    resolvedInitialTab() {
      if (this.initialTabKey) return this.tabIndexOf(this.initialTabKey);
      return this.initialTab || 0;
    },
    activeModuleValue() {
      if (this.previewType === "route") return "rotas";
      if (this.previewType === "fence") return "cercas";
      if (this.previewType === "unit") return "unidades";
      if (this.previewType === "linha") return "linhas";
      if (this.previewType === "area") return "areas";
      const modulo = this.$route && this.$route.query ? this.$route.query.modulo : null;
      return modulo || "linhas";
    },
    emptyStateIcon() {
      const defs = this.recursosOperacaoModuleDefs;
      if (!Array.isArray(defs)) return "mdi-routes";
      const mod = defs.find((item) => item.value === this.activeModuleValue);
      return (mod && mod.icon) || "mdi-routes";
    },
    previewTitle() {
      const items = this.previewItemsResolved;
      if (!items.length) {
        if (this.previewType === "unit") return this.$t("crudUnidades.title");
        if (this.previewType === "fence") return this.$tc("geofence.self", 2);
        if (this.previewType === "route") return this.$t("routs.title");
        if (this.previewType === "linha") return this.$t("crudLinhas.register");
        if (this.previewType === "area") return this.$t("area.title");
        return this.$t("crudLinhas.detail.selectResource");
      }
      if (items.length > 1) {
        return this.$t("crudLinhas.detail.multiSelectedTitle", { count: items.length });
      }
      const item = items[0];
      if (this.previewType === "route") {
        return [item.cod_rota, item.nome_rota].filter(Boolean).join(" — ")
          || this.$t("routs.title");
      }
      if (this.previewType === "fence") {
        return [item.cod_cerca, item.nome_cerca].filter(Boolean).join(" — ")
          || this.$tc("geofence.self", 1);
      }
      if (this.previewType === "unit") {
        return [item.cod_unidade, item.descricao_unidade].filter(Boolean).join(" — ")
          || this.$t("crudUnidades.title");
      }
      if (this.previewType === "area") {
        return [item.cod_area, item.nome_area].filter(Boolean).join(" — ") || this.$t("area.title");
      }
      return "";
    },
    confirmDeleteCercaMessage() {
      const cerca = this.cercaPendenteExclusao || {};
      const linha = this.linha || {};
      const fence = cerca.nome_cerca || cerca.cod_cerca || cerca.id_cerca || "-";
      const line = linha.cod_linha || linha.nome_linha || linha.id_linha || "-";
      return this.$t("crudLinhas.messages.confirmFenceDeletion", { fence, line });
    },
    gruposParaAlocacao() {
      const idLinha = this.linha && this.linha.id_linha;
      return (this.grupos || []).map((g) => ({
        ...g,
        id_linha: idLinha,
        nome:
          g.nome ||
          g.nome_grupo ||
          g.cod_grupo ||
          (g.id_grupo != null ? `Grupo ${g.id_grupo}` : "-"),
      }));
    },
    passageirosVisiveis() {
      const all = this.passageirosLinha || [];
      const s = this.sentidoSelecionado;
      if (s == null || Number(s) === -1) return all;
      if (this.isSentidoIda(s)) {
        return all.filter((p) => Number(p.alocado_ida) === 1);
      }
      if (this.isSentidoVolta(s)) {
        return all.filter((p) => Number(p.alocado_volta) === 1);
      }
      return all;
    },
    rotasVisiveis() {
      const all = this.rotasLinha || [];
      const s = this.sentidoSelecionado;
      if (s == null || Number(s) === -1) return all;
      // Ida/Volta isolados também incluem rotas de Ida e Volta (-1)
      return all.filter((r) => {
        const id = Number(r.id_sentido);
        return id === Number(s) || id === -1;
      });
    },
    cercasVisiveis() {
      const all = this.cercasLinha || [];
      const s = this.sentidoSelecionado;
      if (s == null || Number(s) === -1) return all;
      return all.filter((c) => {
        const id = Number(c.id_sentido);
        return id === Number(s) || id === -1;
      });
    },
    resolvedEntityPreviewRotas() {
      if (this.previewType === "linha") return this.linhaCreatePreviewRotas || [];
      return this.unitPreviewRoutes;
    },
    resolvedEntityPreviewCerca() {
      if (this.previewType === "linha") return this.linhaCreatePreviewCerca;
      return this.unitPreviewFence;
    },
    resolvedEntityPreviewCercasLinha() {
      if (this.previewType === "linha") return this.linhaCreatePreviewCercasLinha || [];
      return [];
    },
    resolvedEntityPreviewFitPreferCerca() {
      return this.previewType === "linha" && this.linhaCreatePreviewPreferUnidade;
    },
    marcadoresPassageiros() {
      return (this.passageirosVisiveis || []).filter(
        (p) =>
          p &&
          p.lat != null &&
          p.lng != null &&
          !Number.isNaN(Number(p.lat)) &&
          !Number.isNaN(Number(p.lng))
      );
    },
    defaultMultiSortCercas() {
      return [
        { value: "ordem_parada", options: { text: this.$t("system.labels.increasing"), value: 1 } },
        { value: "id_tipo_cerca", options: { text: this.$t("system.labels.increasing"), value: 1 } },
      ];
    },
    headersCercasCSV() {
      return [
        { text: this.$t("csv.lineNumber"), value: "line_number", error: "error_line" },
        { text: this.$t("crudLinhas.headers.fences.code"), value: "cod_cerca", error: "error_cod_cerca" },
        { text: this.$t("crudLinhas.headers.fences.type"), value: "cod_tipo_cerca", error: "error_cod_tipo_cerca" },
        { text: this.$t("crudLinhas.headers.fences.direction"), value: "cod_sentido", error: "error_cod_sentido" },
        { text: this.$t("crudLinhas.headers.fences.synoptic"), value: "show_sinotico" },
        { text: this.$t("crudLinhas.headers.fences.order"), value: "ordem_parada" },
        { text: this.$t("crudLinhas.headers.fences.arrivalTime"), value: "tempo_chegada_minutos" },
      ];
    },
    helpCSVCercas() {
      return [
        {
          label: this.$t("crudLinhas.helpCSV.fences.code.label"),
          description: this.$t("crudLinhas.helpCSV.fences.code.description"),
          example: this.$t("crudLinhas.helpCSV.fences.code.example"),
          key: "cod_cerca",
        },
        {
          label: this.$t("crudLinhas.helpCSV.fences.type.label"),
          description: this.$t("crudLinhas.helpCSV.fences.type.code"),
          example: this.$t("crudLinhas.helpCSV.fences.type.example"),
          key: "cod_tipo_cerca",
        },
        {
          label: this.$t("crudLinhas.helpCSV.fences.direction.label"),
          description: this.$t("crudLinhas.helpCSV.fences.direction.description"),
          example: this.$t("crudLinhas.helpCSV.fences.direction.example"),
          key: "cod_sentido",
        },
        {
          label: this.$t("crudLinhas.helpCSV.fences.synoptic.label"),
          description: this.$t("crudLinhas.helpCSV.fences.synoptic.description"),
          example: this.$t("crudLinhas.helpCSV.fences.synoptic.example"),
          key: "show_sinotico",
        },
        {
          label: this.$t("crudLinhas.helpCSV.fences.order.label"),
          description: this.$t("crudLinhas.helpCSV.fences.order.description"),
          example: this.$t("crudLinhas.helpCSV.fences.order.example"),
          key: "ordem_parada",
        },
        {
          label: this.$t("crudLinhas.helpCSV.fences.arrivalTime.label"),
          description: this.$t("crudLinhas.helpCSV.fences.arrivalTime.description"),
          example: this.$t("crudLinhas.helpCSV.fences.arrivalTime.example"),
          key: "tempo_chegada_minutos",
        },
      ];
    },
    headersRotasCSV() {
      return [
        { text: this.$t("crudLinhas.headers.routes.code"), value: "cod_rota" },
        { text: this.$t("crudLinhas.headers.routes.name"), value: "nome_rota" },
        { text: this.$t("crudLinhas.headers.routes.type"), value: "cod_tipo_rota" },
        { text: this.$t("crudLinhas.headers.routes.direction"), value: "cod_sentido" },
      ];
    },
    helpCSVRotas() {
      return [
        {
          label: this.$t("crudLinhas.helpCSV.routes.code.label"),
          description: this.$t("crudLinhas.helpCSV.routes.code.description"),
          example: this.$t("crudLinhas.helpCSV.routes.code.example"),
          key: "cod_rota",
        },
        {
          label: this.$t("crudLinhas.helpCSV.routes.type.label"),
          description: this.$t("crudLinhas.helpCSV.routes.type.description"),
          example: this.$t("crudLinhas.helpCSV.routes.type.example"),
          key: "cod_tipo_rota",
        },
        {
          label: this.$t("crudLinhas.helpCSV.routes.direction.label"),
          description: this.$t("crudLinhas.helpCSV.routes.direction.description"),
          example: this.$t("crudLinhas.helpCSV.routes.direction.example"),
          key: "cod_sentido",
        },
      ];
    },
  },
  watch: {
    tab(val) {
      if (this.parametrosTabIndex >= 0 && val === this.parametrosTabIndex) {
        this.$emit("parameters-tab-opened");
      }
    },
    parametrosTotal() {
      // Parâmetros era a última aba: se o índice atual ficou inválido, volta ao mapa.
      const max = Math.max(0, this.tabKeys.length - 1);
      if (this.tab > max) this.tab = 0;
    },
    showUnidadeComparacaoTab(val, old) {
      // A aba Comparação fica antes de Parâmetros: mantém a aba aberta quando ela aparece/some.
      const antigas = ["map", ...(old ? ["comparison"] : []), ...(this.showParametrosTab ? ["parameters"] : [])];
      const idx = this.tabKeys.indexOf(antigas[this.tab]);
      this.tab = idx >= 0 ? idx : 0;
    },
    previewType: {
      immediate: true,
      handler() {
        this.syncEntityPreview();
      },
    },
    previewItem: {
      immediate: true,
      deep: false,
      handler() {
        this.syncEntityPreview();
      },
    },
    previewItems: {
      deep: false,
      handler() {
        this.syncEntityPreview();
      },
    },
    linha: {
      immediate: true,
      handler(linha) {
        // Consome a flag logo de saída: qualquer early-return abaixo a deixaria
        // pendente e ela suprimiria o reset da troca de linha seguinte.
        const preservarAba = this.preservarAbaProximaLinha;
        this.preservarAbaProximaLinha = false;
        const id = linha && linha.id_linha != null ? Number(linha.id_linha) : null;
        if (!Number.isFinite(id) || id <= 0) {
          this.loadedLinhaId = null;
          if (!preservarAba) this.tab = this.resolvedInitialTab;
          this.sentidoSelecionado = null;
          this.passageirosLinha = [];
          this.candidatosAlocacao = [];
          this.cercasLinha = [];
          this.rotasLinha = [];
          this.resetRotaAssocForm();
          this.resetCercaAssocForm();
          return;
        }
        if (this.loadedLinhaId === id) return;
        this.loadedLinhaId = id;
        if (!preservarAba) this.tab = this.resolvedInitialTab;
        this.sentidoSelecionado = null;
        this.resetRotaAssocForm();
        this.resetCercaAssocForm();
        this.loadPassageiros();
        this.loadCandidatos();
        this.loadCercas();
        this.syncSentidoPadrao();
        this.sentidoAutomatico = this.sentidoSelecionado;
        // As rotas chegam depois do sentido padrão: se a linha só tem rotas de um
        // sentido (ex.: apenas volta), ele passa a ser o selecionado.
        this.loadRotas().then(() => this.ajustarSentidoPelasRotas(id));
      },
    },
    sentidos() {
      const pendente = this.sentidoAutomatico !== undefined
        && Number(this.sentidoSelecionado) === Number(this.sentidoAutomatico);
      this.syncSentidoPadrao();
      if (!pendente) return;
      this.sentidoAutomatico = this.sentidoSelecionado;
      if (!this.loadingRotas) this.ajustarSentidoPelasRotas(this.loadedLinhaId);
    },
    /**
     * O sentido do painel e o contexto de trabalho: os formularios de vinculo o seguem,
     * menos quando estao com um item carregado para edicao.
     */
    sentidoSelecionado(val) {
      if (!this.editingRotaAssoc) this.itemRotaAssoc.id_sentido = val != null ? val : null;
      if (!this.editingCercaAssoc) this.itemCercaAssoc.id_sentido = val != null ? val : null;
    },
  },
  created() {
    // resolvedInitialTab é computed: só disponível a partir daqui, não em data().
    this.tab = this.resolvedInitialTab;
  },
  mounted() {
    this.$el.addEventListener("wheel", this.onPanelWheel, { passive: false });
  },
  beforeDestroy() {
    this.$el.removeEventListener("wheel", this.onPanelWheel);
    this.clearSchedulerPoll();
  },
  methods: {
    onUnidadeLinhasChanged() {
      this.syncEntityPreview();
      this.$emit("unidade-linhas-changed");
    },
    /** Abre o drawer de linhas no mapa e foca o seletor de associação. */
    focarAssociacaoLinhas() {
      this.abrirAbaPorChave("map");
      this.$nextTick(() => {
        const map = this.$refs.entityPreviewMap;
        if (map && typeof map.openLinhasDrawer === "function") map.openLinhasDrawer();
      });
    },
    openCreateDrawer(opts) {
      this.abrirAbaPorChave("map");
      this.$nextTick(() => {
        const map = this.$refs.entityPreviewMap || this.$refs.mapEditor;
        if (map && typeof map.openCreateDrawer === "function") map.openCreateDrawer(opts);
      });
    },
    closeCreateDrawer(opts) {
      const map = this.$refs.entityPreviewMap || this.$refs.mapEditor;
      if (map && typeof map.closeCreateDrawer === "function") map.closeCreateDrawer(opts);
    },
    previewAndFocusCerca(cerca, opts) {
      this.abrirAbaPorChave("map");
      this.$nextTick(() => {
        const map = this.$refs.entityPreviewMap || this.$refs.mapEditor;
        if (map && typeof map.previewAndFocusCerca === "function") {
          map.previewAndFocusCerca(cerca, opts);
        }
      });
    },
    setPickLocationMode(on) {
      this.abrirAbaPorChave("map");
      this.$nextTick(() => {
        const map = this.$refs.entityPreviewMap || this.$refs.mapEditor;
        if (map && typeof map.setPickLocationMode === "function") {
          map.setPickLocationMode(on);
        }
      });
    },
    setDrawPolygonMode(on) {
      this.abrirAbaPorChave("map");
      this.$nextTick(() => {
        const map = this.$refs.entityPreviewMap || this.$refs.mapEditor;
        if (map && typeof map.setDrawPolygonMode === "function") {
          map.setDrawPolygonMode(on);
        }
      });
    },
    setDrawRouteMode(on) {
      this.abrirAbaPorChave("map");
      this.$nextTick(() => {
        const map = this.$refs.entityPreviewMap || this.$refs.mapEditor;
        if (map && typeof map.setDrawRouteMode === "function") {
          map.setDrawRouteMode(on);
        }
      });
    },
    finalizeDrawFenceIfPending() {
      const map = this.$refs.entityPreviewMap || this.$refs.mapEditor;
      if (map && typeof map.finalizeDrawFenceIfPending === "function") {
        return map.finalizeDrawFenceIfPending();
      }
      return false;
    },
    finalizeDrawRouteIfPending() {
      const map = this.$refs.entityPreviewMap || this.$refs.mapEditor;
      if (map && typeof map.finalizeDrawRouteIfPending === "function") {
        return map.finalizeDrawRouteIfPending();
      }
      return false;
    },
    previewAndFocusRota(rota, opts) {
      this.abrirAbaPorChave("map");
      this.$nextTick(() => {
        const map = this.$refs.entityPreviewMap || this.$refs.mapEditor;
        if (map && typeof map.previewAndFocusRota === "function") {
          map.previewAndFocusRota(rota, opts);
        }
      });
    },
    clearCreateDraftRota() {
      const map = this.$refs.entityPreviewMap || this.$refs.mapEditor;
      if (map && typeof map.clearCreateDraftRota === "function") {
        map.clearCreateDraftRota();
      }
    },
    clearCreateDraft() {
      const map = this.$refs.entityPreviewMap || this.$refs.mapEditor;
      if (map && typeof map.clearCreateDraft === "function") {
        map.clearCreateDraft();
      }
    },
    /**
     * Cria uma cerca nova e a associa à linha ativa, recalculando o traçado via vroom
     * (/solve define a ordem, com fallback para /plan). Usado pelo formulário de criação
     * de cerca no drawer da linha (`isLinhasContext`).
     */
    async criarCercaEAssociarNaLinha(payload, opts) {
      const map = this.$refs.mapEditor;
      if (!map || typeof map.criarCercaEAssociarNaLinha !== "function") {
        return { ok: false, message: null };
      }
      return map.criarCercaEAssociarNaLinha(payload, opts);
    },
    setCercaCreateRouteId(idRota) {
      const map = this.$refs.mapEditor;
      if (map && typeof map.setCercaCreateRouteId === "function") {
        map.setCercaCreateRouteId(idRota);
      }
    },
    setLinhaCreatePreviewRotas(rotas) {
      this.abrirAbaPorChave("map");
      this.$nextTick(() => {
        const map = this.$refs.entityPreviewMap;
        if (map && typeof map.setLinhaCreatePreviewRotas === "function") {
          map.setLinhaCreatePreviewRotas(rotas);
        }
      });
    },
    setLinhaCreatePreviewUnidade(unit) {
      this.abrirAbaPorChave("map");
      this.$nextTick(() => {
        const map = this.$refs.entityPreviewMap;
        if (map && typeof map.setLinhaCreatePreviewUnidade === "function") {
          map.setLinhaCreatePreviewUnidade(unit);
        }
      });
    },
    clearLinhaCreatePreview() {
      const map = this.$refs.entityPreviewMap;
      if (map && typeof map.clearLinhaCreatePreview === "function") {
        map.clearLinhaCreatePreview();
      }
    },
    syncEntityPreview() {
      const units = this.previewType === "unit"
        ? this.previewItemsResolved.filter((u) => u && u.id_unidade != null)
        : [];
      if (units.length) {
        this.loadUnitPreview(units);
        return;
      }
      this.unitPreviewLoading = false;
      this.unitPreviewFence = null;
      this.unitPreviewRoutes = [];
      this.unitPreviewGroups = [];
    },
    /** Busca cerca-centro + rotas associadas de cada unidade marcada, em paralelo. */
    async loadUnitPreview(units = []) {
      const list = Array.isArray(units) ? units : [];
      if (!list.length) {
        this.unitPreviewLoading = false;
        this.unitPreviewFence = null;
        this.unitPreviewRoutes = [];
        this.unitPreviewGroups = [];
        return;
      }
      const requestSeq = ++this.unitPreviewRequestSeq;
      this.unitPreviewLoading = true;
      try {
        const groups = await Promise.all(list.map((unit) => this.fetchUnitPreviewGroup(unit)));
        if (requestSeq !== this.unitPreviewRequestSeq) return;
        this.unitPreviewGroups = groups;
        // Campos singulares mantidos por compatibilidade (usados quando só há 1 unidade).
        this.unitPreviewFence = groups.length ? groups[0].cerca : null;
        this.unitPreviewRoutes = groups.length ? groups[0].rotas : [];
      } catch (e) {
        if (requestSeq !== this.unitPreviewRequestSeq) return;
        this.unitPreviewGroups = [];
        this.unitPreviewFence = null;
        this.unitPreviewRoutes = [];
        EventBus.$emit("show-error", { error: e });
      } finally {
        if (requestSeq === this.unitPreviewRequestSeq) {
          this.unitPreviewLoading = false;
        }
      }
    },
    async fetchUnitPreviewGroup(unit) {
      const unitId = Number(unit.id_unidade);
      const tasks = [];
      if (unit.id_cerca_centro != null && unit.id_cerca_centro !== "") {
        tasks.push(
          apiController.request(apiController.endpoints.getCercaporid, {
            params: { id: Number(unit.id_cerca_centro) },
            silent: true,
          })
        );
      } else {
        tasks.push(Promise.resolve({ data: null }));
      }
      tasks.push(
        apiController.request(apiController.endpoints.getLinhasassociadas, {
          data: [unitId],
          silent: true,
        })
      );
      const [cercaRes, linhasRes] = await Promise.all(tasks);
      const cercaRows = Array.isArray(cercaRes.data) ? cercaRes.data : cercaRes.data ? [cercaRes.data] : [];
      const cerca = cercaRows[0] || null;
      const linhas = Array.isArray(linhasRes.data) ? linhasRes.data : [];
      const linhaIds = [...new Set(
        linhas
          .map((linha) => Number(linha && linha.id_linha))
          .filter((id) => Number.isFinite(id) && id > 0)
      )];
      if (!linhaIds.length) {
        return { unit, cerca, rotas: [] };
      }
      const rotasRes = await apiController.request(apiController.endpoints.getRotasporlinha, {
        data: linhaIds,
        silent: true,
      });
      const seen = new Set();
      const rotas = (Array.isArray(rotasRes.data) ? rotasRes.data : []).filter((rota) => {
        const id = Number(rota && rota.id_rota);
        if (!Number.isFinite(id) || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      return { unit, cerca, rotas };
    },
    syncSentidoPadrao() {
      if (!this.sentidos || !this.sentidos.length) {
        this.sentidoSelecionado = null;
        return;
      }
      if (
        this.sentidoSelecionado != null &&
        this.sentidos.some((s) => Number(s.id_sentido) === Number(this.sentidoSelecionado))
      ) {
        return;
      }
      const sentidoLinha = this.resolveSentidoPadraoDaLinha();
      const sentidoDisponivel = sentidoLinha != null
        ? this.sentidos.find((s) => Number(s.id_sentido) === Number(sentidoLinha))
        : null;
      if (sentidoDisponivel) {
        this.sentidoSelecionado = sentidoDisponivel.id_sentido;
        return;
      }
      const ida = this.sentidos.find((s) => Number(s.id_sentido) === 1);
      this.sentidoSelecionado = ida ? ida.id_sentido : this.sentidos[0].id_sentido;
    },
    ajustarSentidoPelasRotas(idLinha) {
      if (this.loadedLinhaId !== idLinha) return;
      if (this.sentidoAutomatico === undefined) return;
      if (Number(this.sentidoSelecionado) !== Number(this.sentidoAutomatico)) {
        this.sentidoAutomatico = undefined;
        return;
      }
      // Sem a lista de sentidos ainda: o watcher de sentidos refaz o ajuste.
      if (!this.sentidos || !this.sentidos.length) return;
      this.sentidoAutomatico = undefined;
      const sentidosRotas = [...new Set(
        (this.rotasLinha || [])
          .map((rota) => Number(rota && rota.id_sentido))
          .filter((sentido) => Number.isFinite(sentido))
      )];
      if (sentidosRotas.length !== 1) return;
      const sentido = (this.sentidos || []).find(
        (s) => Number(s.id_sentido) === sentidosRotas[0]
      );
      if (sentido) this.sentidoSelecionado = sentido.id_sentido;
    },
    resolveSentidoPadraoDaLinha() {
      const linha = this.linha || {};
      const explicit = Number(
        linha.sentido != null ? linha.sentido : linha.id_sentido
      );
      if (explicit === 1 || explicit === 2) return explicit;

      const distanciaIda = Number(linha.distancia_ida) || 0;
      const distanciaVolta = Number(linha.distancia_volta) || 0;
      if (distanciaIda > 0 && distanciaVolta <= 0) return 1;
      if (distanciaVolta > 0 && distanciaIda <= 0) return 2;

      const sentidosVinculados = [...new Set([
        ...(this.rotasLinha || []).map((rota) => Number(rota && rota.id_sentido)),
        ...(this.cercasLinha || []).map((cerca) => Number(cerca && cerca.id_sentido)),
      ].filter((sentido) => sentido === 1 || sentido === 2))];
      return sentidosVinculados.length === 1 ? sentidosVinculados[0] : null;
    },
    onSentidoFromMap(id) {
      if (id == null) return;
      if (Number(this.sentidoSelecionado) === Number(id)) return;
      this.sentidoSelecionado = id;
    },
    /**
     * Abre a aba Mapa e centraliza/ajusta o zoom para enquadrar toda a linha.
     * Chamado ao selecionar uma linha no cadastro de recursos operacionais.
     */
    /**
     * Marca que a próxima troca de linha não deve resetar a aba.
     * Usado ao paginar em Parâmetros, para igualar o comportamento das
     * telas de Rotas/Cercas/Unidades (que usam previewItem e não resetam).
     */
    preservarAbaNaProximaLinha() {
      this.preservarAbaProximaLinha = true;
    },
    /** Reenquadra o mapa sem sair da aba atual. */
    focarMapaSemTrocarAba() {
      this.$nextTick(() => {
        const editor = this.$refs.mapEditor;
        if (editor && typeof editor.scheduleFocusMap === "function") {
          editor.scheduleFocusMap();
        }
      });
    },
    centralizarMapaNaLinha() {
      this.tab = 0;
      this.focarMapaSemTrocarAba();
    },
    mostrarLinhaNoMapa() {
      this.centralizarMapaNaLinha();
    },
    async mostrarRotaNoMapa(item) {
      if (!item) return;
      this.tab = 0;
      if (item.id_sentido != null) {
        this.sentidoSelecionado = item.id_sentido;
      }
      await this.$nextTick();
      const editor = this.$refs.mapEditor;
      if (!editor) return;
      editor.editMode = "editRoute";
      if (typeof editor.carregarDados === "function") {
        await editor.carregarDados();
      }
      await this.$nextTick();
      if (typeof editor.selectRouteForEdit === "function" && item.id_rota != null) {
        editor.selectRouteForEdit(item.id_rota);
        return;
      }
      if (typeof editor.focusRotaOnMap === "function" && item.id_rota != null) {
        editor.focusRotaOnMap(item.id_rota);
      }
    },
    async mostrarCercaNoMapa(item) {
      if (!item) return;
      this.tab = 0;
      if (item.id_sentido != null) {
        this.sentidoSelecionado = item.id_sentido;
      }
      await this.$nextTick();
      const editor = this.$refs.mapEditor;
      if (!editor) return;
      editor.editMode = "editFences";
      if (typeof editor.carregarDados === "function") {
        await editor.carregarDados();
      }
      await this.$nextTick();
      if (typeof editor.editItemCerca === "function") {
        editor.editItemCerca(item);
      }
      if (typeof editor.focusCercaOnMap === "function" && item.id_cerca != null) {
        editor.focusCercaOnMap(item.id_cerca);
      }
    },
    tabIndexOf(key) {
      const i = this.tabKeys.indexOf(key);
      return i < 0 ? 0 : i;
    },
    abrirAbaPorChave(key) {
      if (key === "parameters" && !this.showParametrosTab) return;
      this.tab = this.tabIndexOf(key);
      if (key === "parameters") {
        this.$emit("parameters-tab-opened");
      }
    },
    abrirAbaRotas() {
      this.abrirAbaPorChave("routes");
    },
    abrirAbaCercas() {
      this.abrirAbaPorChave("fences");
    },
    abrirAbaTurnos() {
      this.abrirAbaPorChave("shifts");
    },
    abrirAbaLinhas() {
      this.focarAssociacaoLinhas();
    },
    abrirAbaParametros() {
      if (!this.showParametrosTab) return;
      this.abrirAbaPorChave("parameters");
    },
    /**
     * Dentro do painel: prioriza scroll de áreas internas (tabelas/listas).
     * Se não houver mais o que rolar (ex.: aba Mapa), repassa o wheel para a
     * página — o sticky acompanha até o fim da tabela à esquerda.
     */
    onPanelWheel(e) {
      if (e.ctrlKey || e.metaKey) return;

      const root = this.$el;
      let node = e.target instanceof Element ? e.target : null;
      // Wheel sobre o mapa Leaflet deve zoomar o mapa, não rolar a página
      while (node && node !== root) {
        if (
          node.classList &&
          (node.classList.contains("leaflet-container") ||
            node.classList.contains("linha-map-editor__map"))
        ) {
          return;
        }
        node = node.parentElement;
      }

      node = e.target instanceof Element ? e.target : null;
      while (node && node !== root) {
        if (this.canScrollElement(node, e.deltaY)) {
          return;
        }
        node = node.parentElement;
      }
      if (this.canScrollElement(root, e.deltaY)) {
        return;
      }

      // Fora de áreas scrolláveis do painel → scroll da tela (até o fim da tabela)
      e.preventDefault();
      window.scrollBy({ top: e.deltaY, left: 0, behavior: "auto" });
    },
    canScrollElement(el, deltaY) {
      if (!el || !(el instanceof Element)) return false;
      const style = window.getComputedStyle(el);
      const oy = style.overflowY;
      if (oy !== "auto" && oy !== "scroll" && oy !== "overlay") return false;
      if (el.scrollHeight <= el.clientHeight + 1) return false;
      if (deltaY < 0) return el.scrollTop > 0;
      return el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    },
    async loadPassageiros() {
      const idLinha = this.linha && this.linha.id_linha;
      if (!idLinha) return;
      this.loadingPassageiros = true;
      try {
        const res = await apiController.request(apiController.endpoints.getPessoasporlinha, {
          params: { idlinha: idLinha },
          silent: true,
        });
        this.passageirosLinha = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.passageirosLinha = [];
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.loadingPassageiros = false;
      }
    },
    async loadCandidatos() {
      const idLinha = this.linha && this.linha.id_linha;
      if (!idLinha) {
        this.candidatosAlocacao = [];
        return;
      }
      this.loadingCandidatos = true;
      try {
        const query = { candidatos: 1 };
        const idCliente = this.linha && this.linha.id_cliente;
        if (idCliente != null && idCliente !== "") {
          query.id_cliente = idCliente;
        }
        const res = await apiController.request(apiController.endpoints.getPessoasporlinha, {
          params: { idlinha: idLinha },
          query,
          silent: true,
        });
        this.candidatosAlocacao = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.candidatosAlocacao = [];
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.loadingCandidatos = false;
      }
    },
    rotaAssocSelectText(item) {
      if (!item) return "";
      const cod = item.cod_rota != null ? String(item.cod_rota).trim() : "";
      const nome = item.nome_rota != null ? String(item.nome_rota).trim() : "";
      if (cod && nome) return cod + " — " + nome;
      return cod || nome || (item.id_rota != null ? String(item.id_rota) : "");
    },
    cercaAssocSelectText(item) {
      if (!item) return "";
      const cod = item.cod_cerca != null ? String(item.cod_cerca).trim() : "";
      const nome = item.nome_cerca != null ? String(item.nome_cerca).trim() : "";
      if (cod && nome) return cod + " - " + nome;
      return cod || nome || (item.id_cerca != null ? String(item.id_cerca) : "");
    },
    onRotaAssocSelect(item) {
      this.rotaAssocDetail = item && item.id_rota != null ? item : null;
      this.itemRotaAssoc.id_rota = this.rotaAssocDetail ? Number(item.id_rota) : null;
      this.itemRotaAssoc.cod_integracao =
        this.rotaAssocDetail && this.rotaAssocDetail.cod_integracao != null
          ? this.rotaAssocDetail.cod_integracao
          : null;
    },
    onCercaAssocSelect(item) {
      this.cercaAssocDetail = item && item.id_cerca != null ? item : null;
      this.itemCercaAssoc.id_cerca = this.cercaAssocDetail ? Number(item.id_cerca) : null;
      this.itemCercaAssoc.cod_integracao =
        this.cercaAssocDetail && this.cercaAssocDetail.cod_integracao != null
          ? this.cercaAssocDetail.cod_integracao
          : null;
    },
    onTipoCercaAssocChange() {
      // Ponto inicial e ponto final sempre aparecem no sinotico.
      if (Number(this.itemCercaAssoc.id_tipo_cerca) === 2 || Number(this.itemCercaAssoc.id_tipo_cerca) === 3) {
        this.cercaAssocShowSinotico = true;
      }
    },
    resetRotaAssocForm() {
      this.editingRotaAssoc = false;
      this.rotaAssocOriginal = null;
      this.itemRotaAssoc = {
        id_rota: null,
        id_tipo_rota: null,
        id_sentido: this.sentidoSelecionado != null ? this.sentidoSelecionado : null,
        cod_integracao: null,
      };
      this.rotaAssocDetail = null;
      this.$nextTick(() => {
        const sel = this.$refs.rotaAssocSelect;
        if (sel && typeof sel.resetData === "function") sel.resetData();
      });
    },
    /** Proxima ordem de parada livre, para o formulario ja vir preenchido. */
    proximaOrdemParada() {
      const ordens = (this.cercasLinha || [])
        .map((c) => Number(c.ordem_parada))
        .filter((n) => Number.isFinite(n));
      return ordens.length ? Math.max(...ordens) + 1 : 1;
    },
    resetCercaAssocForm() {
      this.editingCercaAssoc = false;
      this.cercaAssocOriginal = null;
      this.itemCercaAssoc = {
        id_cerca: null,
        id_tipo_cerca: null,
        id_sentido: this.sentidoSelecionado != null ? this.sentidoSelecionado : null,
        ordem_parada: this.proximaOrdemParada(),
        tempo_chegada_minutos: 0,
        cod_integracao: null,
      };
      this.cercaAssocShowSinotico = true;
      this.cercaAssocDetail = null;
      this.$nextTick(() => {
        const sel = this.$refs.cercaAssocSelect;
        if (sel && typeof sel.resetData === "function") sel.resetData();
      });
    },
    normalizeCodIntegracao(valor) {
      if (valor == null || String(valor).trim() === "") return null;
      return String(valor).trim();
    },
    async persistCodIntegracaoCerca(idCerca, codIntegracao) {
      if (idCerca == null) return;
      await apiController.request(apiController.endpoints.updateCerca, {
        data: {
          id_cerca: idCerca,
          cod_integracao: this.normalizeCodIntegracao(codIntegracao),
          cod_integracao_only: true,
        },
        silent: true,
      });
    },
    async persistCodIntegracaoRota(idRota, codIntegracao) {
      if (idRota == null) return;
      await apiController.request(apiController.endpoints.updateRota, {
        data: {
          id_rota: idRota,
          cod_integracao: this.normalizeCodIntegracao(codIntegracao),
          cod_integracao_only: true,
        },
        silent: true,
      });
    },
    /** Sentidos que ocupam a mesma cerca: -1 (ida e volta) colide com qualquer um. */
    cercaSentidosDuplicados(sentido1, sentido2) {
      const primeiro = Number(sentido1);
      const segundo = Number(sentido2);
      return primeiro === segundo || primeiro === -1 || segundo === -1;
    },
    /** Conflito de terminal: dois sentidos que se sobrepoem em IDA ou em VOLTA. */
    sentidosConflictam(sentido1, sentido2) {
      const s1 = Number(sentido1);
      const s2 = Number(sentido2);
      if (s1 === s2) return true;
      const ehIda = (s) => s === 1 || s === 3 || s === 4 || s === -1;
      const ehVolta = (s) => s === 2 || s === 5 || s === 6 || s === -1;
      return (ehIda(s1) && ehIda(s2)) || (ehVolta(s1) && ehVolta(s2));
    },
    /** Carrega a rota da tabela no formulario para alterar tipo/sentido. */
    editRotaAssoc(item) {
      if (!this.canEdit || !item) return;
      this.editingRotaAssoc = true;
      this.rotaAssocOriginal = { id_rota: item.id_rota, id_sentido: item.id_sentido };
      this.itemRotaAssoc = {
        id_rota: item.id_rota,
        id_tipo_rota: item.id_tipo_rota,
        id_sentido: item.id_sentido,
        cod_integracao: item.cod_integracao != null ? item.cod_integracao : null,
      };
      this.rotaAssocDetail = { ...item };
      this.$nextTick(() => {
        const sel = this.$refs.rotaAssocSelect;
        if (sel && typeof sel.selectOption === "function") {
          sel.selectOption({
            id_rota: item.id_rota,
            nome_rota: item.nome_rota,
            cod_rota: item.cod_rota,
          });
        }
      });
    },
    cancelEditRotaAssoc() {
      this.resetRotaAssocForm();
    },
    /** Carrega a cerca da tabela no formulario para alterar tipo/ordem/chegada. */
    editCercaAssoc(item) {
      if (!this.canEdit || !item) return;
      this.editingCercaAssoc = true;
      this.cercaAssocOriginal = {
        id_cerca: item.id_cerca,
        id_sentido: item.id_sentido,
        id_tipo_cerca: item.id_tipo_cerca,
      };
      this.itemCercaAssoc = {
        id_cerca: item.id_cerca,
        id_tipo_cerca: item.id_tipo_cerca,
        id_sentido: item.id_sentido,
        ordem_parada: item.ordem_parada != null ? Number(item.ordem_parada) : 1,
        tempo_chegada_minutos:
          item.tempo_chegada_minutos != null ? Number(item.tempo_chegada_minutos) : 0,
        cod_integracao: item.cod_integracao != null ? item.cod_integracao : null,
      };
      this.cercaAssocShowSinotico = Number(item.show_sinotico) === 1;
      this.cercaAssocDetail = { ...item };
      this.$nextTick(() => {
        const sel = this.$refs.cercaAssocSelect;
        if (sel && typeof sel.selectOption === "function") {
          sel.selectOption({
            id_cerca: item.id_cerca,
            nome_cerca: item.nome_cerca,
            cod_cerca: item.cod_cerca,
          });
        }
      });
    },
    cancelEditCercaAssoc() {
      this.resetCercaAssocForm();
    },
    /** Vincula ou altera uma rota da linha e grava na hora (mesmo fluxo dos turnos). */
    async saveRotaAssoc() {
      if (!this.canEdit || this.savingRotaAssoc) return;
      const { id_rota: idRota, id_tipo_rota: idTipo, id_sentido: idSentido } = this.itemRotaAssoc;
      if (idRota == null) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudLinhas.validation.routeRequired"),
          tipo: "warning",
        });
        return;
      }
      if (idTipo == null) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudLinhas.validation.routeTypeRequired"),
          tipo: "warning",
        });
        return;
      }
      if (idSentido == null || idSentido === "") {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudLinhas.map.selectDirectionFirst"),
          tipo: "warning",
        });
        return;
      }
      const original = this.rotaAssocOriginal;
      const ehOriginal = (r) =>
        !!original &&
        Number(r.id_rota) === Number(original.id_rota) &&
        Number(r.id_sentido) === Number(original.id_sentido);
      const duplicada = (this.rotasLinha || []).some(
        (r) =>
          !ehOriginal(r) &&
          Number(r.id_rota) === Number(idRota) &&
          Number(r.id_sentido) === Number(idSentido)
      );
      if (duplicada) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudLinhas.map.routeAlreadyAssociated"),
          tipo: "error",
        });
        return;
      }
      const detail = this.rotaAssocDetail || { id_rota: idRota };
      const tipo = (this.tiposRota || []).find(
        (t) => Number(t.id_tipo_rota) === Number(idTipo)
      );
      const sentido = (this.sentidos || []).find(
        (s) => Number(s.id_sentido) === Number(idSentido)
      );
      const codIntegracao = this.normalizeCodIntegracao(this.itemRotaAssoc.cod_integracao);
      const nova = {
        ...detail,
        id_rota: Number(idRota),
        id_tipo_rota: idTipo,
        descricao_tipo_rota: tipo ? tipo.descricao_tipo_rota : null,
        id_sentido: idSentido,
        descricao_sentido: sentido ? sentido.descricao_sentido : null,
        cod_integracao: codIntegracao,
        ativa: 1,
      };
      const lista = this.editingRotaAssoc
        ? (this.rotasLinha || []).map((r) => (ehOriginal(r) ? nova : r))
        : [...(this.rotasLinha || []), nova];
      this.savingRotaAssoc = true;
      let ok = false;
      try {
        await this.persistCodIntegracaoRota(idRota, codIntegracao);
        ok = await this.saveBulkRotas(lista);
      } finally {
        this.savingRotaAssoc = false;
      }
      if (ok) this.resetRotaAssocForm();
    },
    /** Vincula ou altera uma cerca da linha e grava na hora (mesmo fluxo dos turnos). */
    async saveCercaAssoc() {
      if (!this.canEdit || this.savingCercaAssoc) return;
      const {
        id_cerca: idCerca,
        id_tipo_cerca: idTipo,
        id_sentido: idSentido,
      } = this.itemCercaAssoc;
      if (idCerca == null) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudLinhas.validation.fenceRequired"),
          tipo: "warning",
        });
        return;
      }
      if (idTipo == null) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudLinhas.validation.fenceTypeRequired"),
          tipo: "warning",
        });
        return;
      }
      if (idSentido == null || idSentido === "") {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudLinhas.map.selectDirectionFirst"),
          tipo: "warning",
        });
        return;
      }
      const ordem = Number(this.itemCercaAssoc.ordem_parada);
      if (!Number.isFinite(ordem) || ordem <= 0) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudLinhas.validation.required"),
          tipo: "warning",
        });
        return;
      }
      const original = this.cercaAssocOriginal;
      const ehOriginal = (c) =>
        !!original &&
        Number(c.id_cerca) === Number(original.id_cerca) &&
        Number(c.id_sentido) === Number(original.id_sentido) &&
        Number(c.id_tipo_cerca) === Number(original.id_tipo_cerca);
      const duplicada = (this.cercasLinha || []).some(
        (c) =>
          !ehOriginal(c) &&
          Number(c.id_cerca) === Number(idCerca) &&
          this.cercaSentidosDuplicados(c.id_sentido, idSentido)
      );
      if (duplicada) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudLinhas.messages.fenceAlreadyExists"),
          tipo: "error",
        });
        return;
      }
      const tipoCerca = (this.tiposCerca || []).find(
        (t) => Number(t.id_tipo_cerca) === Number(idTipo)
      );
      const sentido = (this.sentidos || []).find(
        (s) => Number(s.id_sentido) === Number(idSentido)
      );
      // Ponto inicial e ponto final sao unicos por sentido conflitante.
      if (Number(idTipo) === 2 || Number(idTipo) === 3) {
        const conflitante = (this.cercasLinha || []).some(
          (c) =>
            !ehOriginal(c) &&
            Number(c.id_tipo_cerca) === Number(idTipo) &&
            this.sentidosConflictam(c.id_sentido, idSentido)
        );
        if (conflitante) {
          EventBus.$emit("show-alert", {
            mensagem: this.$t("crudLinhas.messages.conflictDirection", {
              tipoNome: tipoCerca ? tipoCerca.descricao_tipo_cerca : "",
              sentidoNome: sentido ? sentido.descricao_sentido : "",
            }),
            tipo: "error",
          });
          return;
        }
      }
      const detail = this.cercaAssocDetail || {};
      const showSinotico = this.cercaAssocShowSinotico ? 1 : 0;
      const codIntegracao = this.normalizeCodIntegracao(this.itemCercaAssoc.cod_integracao);
      const nova = {
        ...detail,
        id_cerca: Number(idCerca),
        nome_cerca: detail.nome_cerca || null,
        cod_cerca: detail.cod_cerca || null,
        cod_integracao: codIntegracao,
        id_tipo_cerca: idTipo,
        descricao_tipo_cerca: tipoCerca ? tipoCerca.descricao_tipo_cerca : null,
        cod_tipo_cerca: tipoCerca ? tipoCerca.codigo_cerca : null,
        id_sentido: idSentido,
        descricao_sentido: sentido ? sentido.descricao_sentido : null,
        ordem_parada: ordem,
        tempo_chegada_minutos:
          this.itemCercaAssoc.tempo_chegada_minutos != null
            ? Number(this.itemCercaAssoc.tempo_chegada_minutos)
            : 0,
        show_sinotico: showSinotico,
        show_sinotico_extenso:
          showSinotico === 1 ? this.$t("selectOptions.yes") : this.$t("selectOptions.no"),
      };
      // O ponto inicial fica sempre na primeira posicao e o final na ultima.
      if (
        wouldBreakTerminusOrder(this.cercasLinha || [], nova, original, {
          rotas: this.rotasLinha,
        })
      ) {
        this.dialogTerminusOrder = true;
        return;
      }
      const lista = this.editingCercaAssoc
        ? (this.cercasLinha || []).map((c) => (ehOriginal(c) ? nova : c))
        : [...(this.cercasLinha || []), nova];
      this.savingCercaAssoc = true;
      let ok = false;
      try {
        await this.persistCodIntegracaoCerca(idCerca, codIntegracao);
        ok = await this.saveBulkCercas(lista);
      } finally {
        this.savingCercaAssoc = false;
      }
      if (ok) this.resetCercaAssocForm();
    },
    async loadCercas() {
      const idLinha = this.linha && this.linha.id_linha;
      if (!idLinha) return;
      this.loadingCercas = true;
      try {
        const res = await apiController.request(apiController.endpoints.getCercasporlinha, {
          data: [idLinha],
          silent: true,
        });
        const rows = Array.isArray(res.data) ? res.data : [];
        this.cercasLinha = rows.map((c) => ({
          ...c,
          show_sinotico_extenso:
            Number(c.show_sinotico) === 1
              ? this.$t("selectOptions.yes")
              : this.$t("selectOptions.no"),
        }));
      } catch (e) {
        this.cercasLinha = [];
      } finally {
        this.loadingCercas = false;
      }
    },
    async loadRotas() {
      const idLinha = this.linha && this.linha.id_linha;
      if (!idLinha) return;
      this.loadingRotas = true;
      try {
        // Mesma fonte do mapa (linhas_rotas), não só rotas_vigencia.padrao
        const res = await apiController.request(apiController.endpoints.getRotasporlinha, {
          data: [idLinha],
          silent: true,
        });
        const routes = Array.isArray(res.data) ? res.data : [];
        const preferredOrder = new Map(
          (this.preferredRouteIds || []).map((id, index) => [Number(id), index])
        );
        this.rotasLinha = routes.slice().sort((a, b) => {
          const aOrder = preferredOrder.has(Number(a.id_rota))
            ? preferredOrder.get(Number(a.id_rota))
            : Number.MAX_SAFE_INTEGER;
          const bOrder = preferredOrder.has(Number(b.id_rota))
            ? preferredOrder.get(Number(b.id_rota))
            : Number.MAX_SAFE_INTEGER;
          return aOrder - bOrder;
        });
      } catch (e) {
        this.rotasLinha = [];
      } finally {
        this.loadingRotas = false;
      }
    },
    editRotaFromTable(item) {
      this.tab = 0;
      this.$nextTick(() => {
        const editor = this.$refs.mapEditor;
        if (!editor) return;
        editor.editMode = "editRoute";
        if (item && item.id_sentido != null) {
          this.sentidoSelecionado = item.id_sentido;
        }
        if (typeof editor.focusRotaOnMap === "function" && item && item.id_rota != null) {
          this.$nextTick(() => editor.focusRotaOnMap(item.id_rota));
        }
      });
    },
    async deleteRotaFromTable(item) {
      const idLinha = this.linha && this.linha.id_linha;
      if (!idLinha || !item) return;
      const remaining = this.rotasLinha.filter(
        (r) =>
          !(
            Number(r.id_rota) === Number(item.id_rota) &&
            Number(r.id_sentido) === Number(item.id_sentido)
          )
      );
      try {
        await apiController.request(apiController.endpoints.deleteRotaslinhabulk, {
          data: { id_linha: idLinha },
          silent: true,
        });
        if (remaining.length) {
          const rows = remaining.map((r) => [
            idLinha,
            r.id_rota,
            r.id_tipo_rota,
            r.id_sentido,
            1,
          ]);
          await apiController.request(apiController.endpoints.insertRotaslinhabulk, {
            data: rows,
            silent: true,
          });
        }
        await this.persistHistoricoExclusaoRota(item);
        await this.loadRotas();
        const editor = this.$refs.mapEditor;
        if (editor && typeof editor.carregarDados === "function") {
          editor.carregarDados();
        }
        this.$emit("rotas-changed");
        EventBus.$emit("show-success", {
          message: this.$t("crudLinhas.map.routesSaved"),
        });
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      }
    },
    editCercaFromTable(item) {
      this.tab = 0;
      this.$nextTick(() => {
        const editor = this.$refs.mapEditor;
        if (!editor) return;
        editor.editMode = "editFences";
        if (typeof editor.editItemCerca === "function") {
          editor.editItemCerca(item);
        }
      });
    },
    deleteCercaFromTable(item) {
      if (!item) return;
      this.cercaPendenteExclusao = { ...item };
      this.confirmDeleteCercaDialog = true;
    },
    cancelDeleteCerca() {
      this.confirmDeleteCercaDialog = false;
      this.cercaPendenteExclusao = null;
    },
    async confirmDeleteCerca() {
      const idLinha = this.linha && this.linha.id_linha;
      const item = this.cercaPendenteExclusao;
      this.confirmDeleteCercaDialog = false;
      this.cercaPendenteExclusao = null;
      if (!idLinha || !item) return;
      const cercasAntes = this.cercasLinha.map((c) => ({ ...c }));
      const remaining = this.cercasLinha.filter(
        (c) =>
          !(
            Number(c.id_cerca) === Number(item.id_cerca) &&
            Number(c.id_sentido) === Number(item.id_sentido) &&
            Number(c.id_tipo_cerca) === Number(item.id_tipo_cerca)
          )
      );
      try {
        await apiController.request(apiController.endpoints.deleteCercasbylinha, {
          data: { id_linha: idLinha },
          silent: true,
        });
        if (remaining.length) {
          const rows = remaining.map((c) => [
            idLinha,
            c.id_cerca,
            c.id_tipo_cerca,
            c.show_sinotico ? 1 : 0,
            c.tempo_chegada_minutos || 0,
            c.id_sentido,
            c.ordem_parada || 0,
          ]);
          await apiController.request(apiController.endpoints.insertCercaslinhabulk, {
            data: rows,
            silent: true,
          });
        }
        await this.persistHistoricoExclusaoCerca(item, cercasAntes, remaining);
        await this.loadCercas();
        const editor = this.$refs.mapEditor;
        if (editor && typeof editor.carregarDados === "function") {
          editor.carregarDados();
        }
        this.$emit("cercas-changed");
        EventBus.$emit("show-success", {
          message: this.$t("crudLinhas.map.fencesSaved"),
        });
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      }
    },
    async persistHistoricoExclusaoCerca(item, cercasAntes, cercasDepois) {
      let user = null;
      try {
        user = JSON.parse(sessionStorage.getItem("user"));
      } catch (e) {
        user = null;
      }
      const destinationIds = [...(cercasAntes || []), ...(cercasDepois || [])]
        .filter((c) => c && (Number(c.id_tipo_cerca) === 2 || Number(c.id_tipo_cerca) === 3))
        .map((c) => c.id_cerca);
      const result = await persistHistoricoRoteirizacaoCrudEvent({
        idSessaoOrigem: this.historicoSessaoOrigemId,
        idSessaoCrud: this.historicoSessaoId,
        idCercaDestinoCandidates: destinationIds,
        idUsuario: user && user.id_usuario,
        detalhe: {
          origem: "CRUD_LINHAS",
          acao: "EXCLUIR_CERCA",
          id_linha: this.linha && this.linha.id_linha,
          cod_linha: this.linha && this.linha.cod_linha,
          nome_linha: this.linha && this.linha.nome_linha,
          cerca_anterior: { ...item },
          cercas_antes: (cercasAntes || []).map((c) => ({ ...c })),
          cercas_depois: (cercasDepois || []).map((c) => ({ ...c })),
        },
      });
      if (!result || !result.ok) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudLinhas.messages.historySaveError"),
          tipo: "warning",
        });
      }
    },
    /** Regrava rotas_linhas com a lista informada. Retorna se a gravacao ocorreu. */
    async saveBulkRotas(items) {
      const idLinha = this.linha && this.linha.id_linha;
      if (!idLinha || !this.canEdit) return false;
      const rowsItems = Array.isArray(items) ? items : [];
      try {
        await apiController.request(apiController.endpoints.deleteRotaslinhabulk, {
          data: { id_linha: idLinha },
          silent: true,
        });
        if (rowsItems.length) {
          const rows = rowsItems.map((r) => [
            idLinha,
            r.id_rota,
            r.id_tipo_rota,
            r.id_sentido,
            1,
          ]);
          await apiController.request(apiController.endpoints.insertRotaslinhabulk, {
            data: rows,
            silent: true,
          });
        }
        await this.loadRotas();
        const editor = this.$refs.mapEditor;
        if (editor && typeof editor.carregarDados === "function") {
          editor.carregarDados();
        }
        this.$emit("rotas-changed");
        EventBus.$emit("show-success", {
          message: this.$t("crudLinhas.map.routesSaved"),
        });
        return true;
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
        return false;
      }
    },
    /** Regrava cercas_linhas com a lista informada. Retorna se a gravacao ocorreu. */
    async saveBulkCercas(items) {
      const idLinha = this.linha && this.linha.id_linha;
      if (!idLinha || !this.canEdit) return false;
      const rowsItems = Array.isArray(items) ? items : [];
      if (hasTerminusOrderViolation(rowsItems, { rotas: this.rotasLinha })) {
        this.dialogTerminusOrder = true;
        return false;
      }
      try {
        await apiController.request(apiController.endpoints.deleteCercasbylinha, {
          data: { id_linha: idLinha },
          silent: true,
        });
        if (rowsItems.length) {
          const rows = rowsItems.map((c) => [
            idLinha,
            c.id_cerca,
            c.id_tipo_cerca,
            c.show_sinotico ? 1 : 0,
            c.tempo_chegada_minutos || 0,
            c.id_sentido,
            c.ordem_parada || 0,
          ]);
          await apiController.request(apiController.endpoints.insertCercaslinhabulk, {
            data: rows,
            silent: true,
          });
        }
        await this.loadCercas();
        const editor = this.$refs.mapEditor;
        if (editor && typeof editor.carregarDados === "function") {
          editor.carregarDados();
        }
        this.$emit("cercas-changed");
        EventBus.$emit("show-success", {
          message: this.$t("crudLinhas.map.fencesSaved"),
        });
        return true;
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
        return false;
      }
    },
    /**
     * Grupos só são usados nos diálogos de alocação; carrega na primeira abertura
     * (lista não depende da linha) paginando o endpoint lazy, sem as contagens do getallgrupos.
     */
    async loadGrupos() {
      if (this.gruposCarregados) return;
      const pageSize = 200;
      const grupos = [];
      try {
        for (let page = 1; ; page += 1) {
          const res = await apiController.request(apiController.endpoints.getGruposlazy, {
            data: { page, pageSize, search: "", includeTotal: true },
            silent: true,
          });
          const items = (res.data && res.data.items) || [];
          grupos.push(...items);
          const total = Number(res.data && res.data.total);
          if (!items.length || (Number.isFinite(total) && grupos.length >= total)) break;
        }
        this.grupos = grupos;
        this.gruposCarregados = true;
      } catch (e) {
        this.grupos = [];
      }
    },
    onCercasChanged() {
      this.loadCercas();
      this.$emit("cercas-changed");
    },
    onRotasChanged() {
      this.loadRotas();
      this.$emit("rotas-changed");
    },
    onPassengerClick(pax) {
      if (!pax) return;
      this.openEditAllocation(pax);
    },
    async openBulkAllocation() {
      this.passageiroSelecionado = null;
      this.loadCandidatos();
      await this.loadGrupos();
      this.allocationDialog = true;
    },
    openGroupAllocation() {
      this.groupAllocationDialog = true;
    },
    async openEditAllocation(pax) {
      this.passageiroSelecionado = pax;
      await this.loadGrupos();
      this.allocationDialog = true;
    },
    onAllocated() {
      this.loadPassageiros();
      this.loadCandidatos();
      this.$emit("passengers-changed");
    },
    /**
     * Após verificar no diálogo de Linhas, abre o RoteirizacaoAlocacaoDialog
     * (confirmações, cerca valido=3 e PCD /solve).
     */
    async onProceedAllocation({ selection, validatedAddresses, pollProcess } = {}) {
      const sel = Array.isArray(selection) ? selection : [];
      if (!sel.length) return;
      this.allocationValidatedAddresses = Array.isArray(validatedAddresses)
        ? validatedAddresses
        : sel;
      this.allocationPollProcess =
        typeof pollProcess === "function" ? pollProcess : null;
      await this.$nextTick();
      const dlg = this.$refs.roteirizacaoAllocationDialog;
      if (!dlg || typeof dlg.open !== "function") return;
      await dlg.open(sel, { skipRejectedConfirm: true });
    },
    pollSchedulerForAllocation(opts) {
      if (typeof this.allocationPollProcess === "function") {
        return this.allocationPollProcess(opts);
      }
      return this.pollSchedulerProcess(opts);
    },
    lineCapacityFallback() {
      const lot = this.linha && Number(this.linha.lotacao_prevista);
      return Number.isFinite(lot) && lot > 0 ? lot : null;
    },
    onRoteirizacaoAllocationSaved() {
      this.allocationValidatedAddresses = [];
      this.allocationPollProcess = null;
      this.onAllocated();
      this.onCercasChanged();
      this.onRotasChanged();
    },
    clearSchedulerPoll() {
      if (this.schedulerPoll && this.schedulerPoll.timer != null) {
        clearInterval(this.schedulerPoll.timer);
        this.schedulerPoll.timer = null;
      }
      if (this.schedulerPoll) this.schedulerPoll.inFlight = false;
    },
    pollSchedulerProcess({ idProcesso, waitMs = 30 * 60 * 1000, pollMs = 5000, onDone, onError }) {
      this.clearSchedulerPoll();
      if (!this.schedulerPoll) {
        this.schedulerPoll = { timer: null, inFlight: false };
      }
      const deadline = Date.now() + waitMs;
      const failedMsg = this.$t("crudLinhas.detail.allocation.verifyError");

      const pollOnce = () => {
        if (Date.now() > deadline) {
          this.clearSchedulerPoll();
          if (typeof onError === "function") onError(failedMsg);
          return;
        }
        if (this.schedulerPoll.inFlight) return;
        this.schedulerPoll.inFlight = true;

        apiController.request(apiController.endpoints.schedulerGetProcessoschedulerbyid, {
          data: { id_processo: idProcesso },
          config: { timeout: 30000 },
          silent: true,
          onSuccess: (statusRes) => {
            this.schedulerPoll.inFlight = false;
            const pollData = statusRes.data || {};
            if (pollData.erro) {
              this.clearSchedulerPoll();
              if (typeof onError === "function") onError(pollData.mensagem || failedMsg);
              return;
            }
            const processo = pollData.processo;
            const status = processo && processo.status;
            if (status === "PENDING" || status === "EXECUTING") return;
            this.clearSchedulerPoll();
            if (status === "FAILED" || status === "CANCELED") {
              if (typeof onError === "function") {
                onError((processo && processo.mensagem) || failedMsg);
              }
              return;
            }
            let resultado = null;
            try {
              resultado =
                processo && processo.resultado != null
                  ? typeof processo.resultado === "string"
                    ? JSON.parse(processo.resultado)
                    : processo.resultado
                  : null;
            } catch (_) {
              resultado = null;
            }
            if (typeof onDone === "function") onDone(resultado);
          },
          onError: (e) => {
            this.schedulerPoll.inFlight = false;
            this.clearSchedulerPoll();
            const msg =
              (e.response &&
                e.response.data &&
                (e.response.data.message || e.response.data.mensagem)) ||
              e.message ||
              failedMsg;
            if (typeof onError === "function") onError(msg);
          },
        });
      };

      this.schedulerPoll.timer = setInterval(pollOnce, pollMs);
      pollOnce();
    },
    isSentidoIda(idSentido) {
      const n = Number(idSentido);
      return n === 1 || n === 3 || n === 4;
    },
    isSentidoVolta(idSentido) {
      const n = Number(idSentido);
      return n === 2 || n === 5 || n === 6;
    },
    async onDeallocate(pax) {
      if (!pax || !pax.id_pessoa) return;
      const s = this.sentidoSelecionado;
      const stillOnOtherSense =
        (this.isSentidoIda(s) && Number(pax.alocado_volta) === 1) ||
        (this.isSentidoVolta(s) && Number(pax.alocado_ida) === 1);
      const keepCercas = stillOnOtherSense;
      const row = {
        id_pessoa: pax.id_pessoa,
        id_grupo: pax.id_grupo,
        id_cerca_embarque: keepCercas
          ? pax.id_cerca_embarque != null
            ? pax.id_cerca_embarque
            : null
          : null,
        id_cerca_desembarque: keepCercas
          ? pax.id_cerca_desembarque != null
            ? pax.id_cerca_desembarque
            : null
          : null,
      };

      if (this.isSentidoIda(s)) {
        row.id_linha_ida = null;
        row.update_linha_volta = false;
      } else if (this.isSentidoVolta(s)) {
        row.id_linha_volta = null;
        row.update_linha_ida = false;
      } else {
        // sentido -1 / null: limpa ida e volta
        row.id_linha_ida = null;
        row.id_linha_volta = null;
      }

      try {
        await apiController.request(apiController.endpoints.alocaPessoasemlinhas, {
          data: {
            passageiros: [row],
            historico_sessao_id: this.historicoSessaoId || null,
            historico_sessao_origem_id: this.historicoSessaoOrigemId || null,
            historico_origem: "CRUD_LINHAS",
            historico_linha_id: this.linha && this.linha.id_linha,
          },
          silent: true,
        });
        this.onAllocated();
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      }
    },
    async persistHistoricoExclusaoRota(item) {
      let user = null;
      try {
        user = JSON.parse(sessionStorage.getItem("user"));
      } catch (e) {
        user = null;
      }
      const destinationIds = (this.cercasLinha || [])
        .filter((c) => c && [2, 3].includes(Number(c.id_tipo_cerca)))
        .map((c) => c.id_cerca);
      const result = await persistHistoricoRoteirizacaoCrudEvent({
        idSessaoOrigem: this.historicoSessaoOrigemId,
        idSessaoCrud: this.historicoSessaoId,
        idCercaDestinoCandidates: destinationIds,
        idUsuario: user && user.id_usuario,
        tipoEvento: "EDITADA_ROTA",
        detalhe: {
          origem: "CRUD_LINHAS",
          acao: "EXCLUIR_ROTA",
          id_linha: this.linha && this.linha.id_linha,
          cod_linha: this.linha && this.linha.cod_linha,
          nome_linha: this.linha && this.linha.nome_linha,
          id_sentido: item && item.id_sentido,
          rota_atual: item ? { ...item } : null,
        },
      });
      if (!result || !result.ok) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudLinhas.messages.historySaveError"),
          tipo: "warning",
        });
      }
    },
    requestDeallocate(pax) {
      this.$emit('deallocate-passenger', pax);
    },
  },
};
</script>

<style scoped>
/* Sticky fica no wrapper em RecursosOperacionaisSplitLayout; aqui só preenche o espaço da viewport */
.linha-detalhe-panel {
  height: 100%;
  max-height: 100%;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  border-radius: 10px;
  overflow: hidden;
  font-size: 0.75rem;
  box-sizing: border-box;
}
.linha-detalhe-panel >>> .v-toolbar__title,
.linha-detalhe-panel >>> .subtitle-1 {
  font-size: 0.85rem !important;
  line-height: 1.3;
}
.linha-detalhe-panel >>> .v-tab {
  font-size: 0.75rem !important;
  min-width: 0;
  padding: 0 10px;
}
.linha-detalhe-panel__tabs-bar {
  max-width: 100%;
  min-width: 0;
  background-color: #f5f5f5;
}
.linha-detalhe-panel__tabs-bar >>> .v-tabs {
  max-width: 100%;
  background-color: transparent !important;
}
.linha-detalhe-panel__sentido {
  width: 160px;
  max-width: 40%;
  padding-top: 4px;
  padding-bottom: 4px;
  background-color: #f5f5f5;
}
.linha-detalhe-panel >>> .v-tabs {
  max-width: 100%;
}
.linha-detalhe-panel >>> .v-slide-group__content {
  max-width: 100%;
}
.linha-detalhe-panel >>> .v-label,
.linha-detalhe-panel >>> .v-select__selection,
.linha-detalhe-panel >>> .v-list-item__title,
.linha-detalhe-panel >>> .v-btn,
.linha-detalhe-panel >>> .caption,
.linha-detalhe-panel >>> .subtitle-2 {
  font-size: 0.75rem !important;
}
.linha-detalhe-panel >>> .v-input {
  font-size: 0.75rem;
}
.linha-detalhe-panel >>> .v-text-field--outlined.v-input--dense .v-input__slot,
.linha-detalhe-panel >>> .v-select--outlined.v-input--dense .v-input__slot {
  min-height: 32px !important;
}
.linha-detalhe-panel >>> .v-data-table {
  font-size: 0.75rem;
  max-width: 100%;
}
.linha-detalhe-panel >>> .v-data-table .v-data-table-header th,
.linha-detalhe-panel >>> .v-data-table td {
  font-size: 0.75rem;
  padding: 0 6px !important;
  height: 32px !important;
}
.linha-detalhe-panel__tabs {
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.linha-detalhe-panel__tabs >>> .v-window {
  height: 100%;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
}
.linha-detalhe-panel__tabs >>> .v-window__container,
.linha-detalhe-panel__tabs >>> .v-window-item {
  height: 100%;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
}
.linha-detalhe-panel__tabs >>> .v-window-item--active {
  display: flex;
  flex-direction: column;
}
.linha-detalhe-panel__tab-item {
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 auto;
}
/* Mapa ocupa 100% sem scroll interno */
.linha-detalhe-panel__tab-item--map {
  height: 100%;
  overflow: hidden;
}
.linha-detalhe-panel__map-wrap {
  height: 100%;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  flex: 1 1 auto;
}
.linha-detalhe-panel__map-editor {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}
.linha-detalhe-panel__preview {
  min-height: 0;
  overflow: hidden;
}
.linha-detalhe-panel__preview-map {
  min-height: 0;
}
/* Grade/Passageiros: scroll até o fim da tabela dentro do painel */
.linha-detalhe-panel__tab-item--scroll {
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
</style>
